import type { DayCard } from '../../api/types'
import { VehicleIcon } from '../../components/VehicleIcon'
import { calendarDayText, calendarSpanText, uhrText, type Uhr } from '../../lib/ortszeit'
import { sectionZoneMarks, zoneMarkAt } from '../../lib/zonen'
import { useTexte } from '../../texte'

/**
 * The day card in the chat (#79, 0.40.0): what a day of the route amounted
 * to -- distance, time in motion, climb, start and end, the points of the
 * day. The map of the day is the post's photo and stands below the card
 * like every photo; this card carries the numbers.
 *
 * Every number comes from the server, computed from the GPX files there;
 * the browser only formats. Start and end are shown in the trip's zone
 * course at their own moments (`versatz`, 1.87.1) -- the local time where
 * the route was, not the clock of the device that posted the card.
 * **What is missing is left
 * out**: a recording without elevation has no metres of climb, and the
 * card does not say zero.
 *
 * **Over several days (1.151.0)** the title names the span, and the rows
 * are one per day instead of the sections. Whether rows stand at all is
 * the card's own choice (`show_rows`), off for a new one; a card from
 * before has no field and shows them, as it always did.
 */
export function Tageskarte({ karte, versatz = null }: { karte: DayCard; versatz?: Uhr }) {
  const texte = useTexte()
  const t = texte.tageskarte
  // The sections of the day (1.98.0): from use, "die einzelnen Segmente
  // getrennt ausweisen". One section says nothing the tiles do not, and a
  // card from before has none.
  const rows = karte.show_rows !== false
  const days = rows ? karte.days ?? [] : []
  const sections = rows && days.length === 0 && (karte.sections ?? []).length > 1 ? karte.sections! : []
  const spanne = (karte.day_to ?? null) !== null && karte.day_to !== karte.day
  // A time in another zone than the start says which (1.100.0, `zoneMarkAt`).
  const withZone = (time: string, city: string | null) => (city === null ? time : t.timeInZone(time, city))
  const zeile = (wann: string | null, ort: string) => {
    if (wann === null) return null
    const zeit = withZone(uhrText(wann, versatz), zoneMarkAt(versatz, karte.started_at, wann))
    return ort === '' ? zeit : t.ortUmZeit(zeit, ort)
  }
  const start = zeile(karte.started_at, karte.start_place)
  const ziel = zeile(karte.ended_at, karte.end_place)

  return (
    <div className="tageskarte" data-tageskarte={karte.id} data-tag={karte.day} data-tag-bis={spanne ? karte.day_to! : undefined}>
      <h3 className="tageskarte__titel">{spanne ? t.titelSpanne(calendarSpanText(karte.day, karte.day_to!)) : t.titel(calendarDayText(karte.day))}</h3>
      <div className="tageskarte__kacheln">
        <div className="tageskarte__kachel" data-wert="strecke">
          <span className="tageskarte__zahl">{t.km(karte.distance_m / 1000)}</span>
          <span className="tageskarte__name">{t.strecke}</span>
        </div>
        {karte.moving_s > 0 && (
          <div className="tageskarte__kachel" data-wert="fahrzeit">
            <span className="tageskarte__zahl">{t.dauer(karte.moving_s)}</span>
            <span className="tageskarte__name">{t.fahrzeit}</span>
          </div>
        )}
        {(karte.ascent_m !== null || karte.descent_m !== null) && (
          <div className="tageskarte__kachel" data-wert="hoehenmeter">
            {/* Up and down on two lines: "↑ 1.240 m · ↓ 980 m" in one line
                did not fit a tile of 167 points (measured at 390). */}
            <span className="tageskarte__zahl tageskarte__zahl--zwei">
              <span>{t.bergauf(Math.round(karte.ascent_m ?? 0))}</span>
              <span>{t.bergab(Math.round(karte.descent_m ?? 0))}</span>
            </span>
            <span className="tageskarte__name">{t.hoehenmeter}</span>
          </div>
        )}
        {karte.max_ele_m !== null && (
          <div className="tageskarte__kachel" data-wert="hoechster">
            <span className="tageskarte__zahl">{t.meter(Math.round(karte.max_ele_m))}</span>
            <span className="tageskarte__name">{t.hoechsterPunkt}</span>
          </div>
        )}
      </div>
      {(start !== null || ziel !== null) && (
        <dl className="tageskarte__wege">
          {start !== null && (
            <div>
              <dt>{t.start}</dt>
              <dd>{start}</dd>
            </div>
          )}
          {ziel !== null && (
            <div>
              <dt>{t.ziel}</dt>
              <dd>{ziel}</dd>
            </div>
          )}
        </dl>
      )}
      {days.length > 0 && (
        <ol className="tageskarte__sections" aria-label={t.tage}>
          {days.map((d) => (
            <li key={d.day} className="tageskarte__section" data-day-row={d.day}>
              <span className="tageskarte__section-text">
                <span>{t.tagZeile(calendarDayText(d.day), t.km(d.distance_m / 1000), t.dauer(d.moving_s))}</span>
              </span>
            </li>
          ))}
        </ol>
      )}
      {sections.length > 0 && (
        <ol className="tageskarte__sections" aria-label={t.sections}>
          {sections.map((s, i) => {
            const places = t.sectionPlaces(s.start_place, s.end_place)
            const seconds = Math.max(0, (Date.parse(s.to) - Date.parse(s.from)) / 1000)
            const [zoneFrom, zoneTo] = sectionZoneMarks(versatz, karte.started_at, s.from, s.to)
            return (
              <li key={`${s.from}-${i}`} className="tageskarte__section" data-section={i} data-vehicle={s.vehicle}>
                <VehicleIcon vehicle={s.vehicle} size={28} className="tageskarte__vehicle" />
                <span className="visually-hidden">{texte.karte.sections.vehicle[s.vehicle]}</span>
                <span className="tageskarte__section-text">
                  <span>{t.sectionLine(withZone(uhrText(s.from, versatz), zoneFrom), withZone(uhrText(s.to, versatz), zoneTo), t.km(s.distance_m / 1000), t.dauer(seconds))}</span>
                  {places !== '' && <span className="tageskarte__section-places">{places}</span>}
                </span>
              </li>
            )
          })}
        </ol>
      )}
      {karte.highlights.length > 0 && (
        <p className="tageskarte__highlights">
          <span className="tageskarte__name">{t.highlights}</span> {karte.highlights.join(t.trenner)}
        </p>
      )}
    </div>
  )
}
